import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Eye,
  Check,
  X,
  Clock,
  AlertCircle,
  ChevronDown,
  RefreshCw,
  User,
  Package,
  IndianRupee,
  Calendar,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
} from "lucide-react";
import { requestOrderService } from "@/services/api/request.order.service";
import { toast } from "react-toastify";

const AdminApprovalDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [notificationWarning, setNotificationWarning] = useState(null);

  // Filters and pagination
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    search: "",
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [calculatedValues, setCalculatedValues] = useState({
    totalAmount: 0,
    pricePerShare: 0,
    quantity: 0,
    originalTotalAmount: 0,
    difference: 0,
    differencePercentage: 0,
  });
  // Modal states
  const [actionType, setActionType] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [actionData, setActionData] = useState({
    customBuyPrice: "",
    notes: "",
    customQuantity: "",
    totalInvestment: "",
    currentValue: "",
    totalProfit: "",
    profitPercentage: "",
    lastValuationDate: "",
    paymentMethod: "",
    paymentReference: "",
    bankAccountNumber: "",
    accountHolderName: "",
    ifscCode: "",
    bankName: "",
    processImmediately: false,
  });

  // Fetch approval requests (includes stats in response)
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await requestOrderService.getAllusersApprovalRequests(
        filters
      );

      // Handle your API response structure
      if (response.success) {
        setRequests(response.data || []);
        setStats(response.statistics || {});
        setPagination(response.pagination || {});
      } else {
        throw new Error("Failed to fetch requests");
      }
    } catch (err) {
      const errorMsg = err.error || err.message || "Failed to fetch approval requests";
      toast.error(errorMsg);
      setRequests([]);
      setStats({});
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch request details
  const fetchRequestDetails = async (requestId) => {
    try {
      setDetailsLoading(true);
      const response = await requestOrderService.getApprovalRequestDetails(
        requestId
      );

      if (response.success) {
        setSelectedRequest(response.data);
        setShowDetailsModal(true);
      } else {
        throw new Error("Failed to fetch request details");
      }
    } catch (err) {
      toast.error(err.error || err.message || "Failed to fetch request details");
    } finally {
      setDetailsLoading(false);
    }
  };
  const calculateModalValues = () => {
    if (!selectedRequest) return;

    const originalPrice = selectedRequest.product?.buyPrice || 0;
    const originalQuantity = selectedRequest.requestedQuantity || 0;
    const originalTotal = selectedRequest.totalAmount || 0;

    // Get current values from form
    const currentPrice = parseFloat(actionData.customBuyPrice) || originalPrice;
    const currentQuantity =
      parseInt(actionData.customQuantity) || originalQuantity;

    // Calculate new values
    const newTotalAmount = currentPrice * currentQuantity;
    const difference = newTotalAmount - originalTotal;
    const differencePercentage =
      originalTotal > 0 ? (difference / originalTotal) * 100 : 0;

    setCalculatedValues({
      totalAmount: newTotalAmount,
      pricePerShare: currentPrice,
      quantity: currentQuantity,
      originalTotalAmount: originalTotal,
      difference: difference,
      differencePercentage: differencePercentage,
    });
  };

  useEffect(() => {
    if (showModal && selectedRequest) {
      calculateModalValues();
    }
  }, [
    actionData.customBuyPrice,
    actionData.customQuantity,
    selectedRequest,
    showModal,
  ]);
  // Handle approve request
  const handleApprove = async () => {
    if (!selectedRequest) return;
    if (!actionData.paymentMethod) {
      toast.warning("Please select a payment method.");
      return;
    }

    // Validation for Offline Payment Bank Account
    if (actionData.paymentMethod === "OFFLINE") {
      const bankAc = actionData.bankAccountNumber?.trim() || "";
      if (!bankAc) {
        toast.warning("Bank account number is required for offline payments.");
        return;
      }

      // Bank Account validation (9 to 18 digits)
      const bankAcRegex = /^\d{9,18}$/;
      if (!bankAcRegex.test(bankAc)) {
        toast.error("Invalid bank account number. Please enter 9 to 18 digits.");
        return;
      }

      // IFSC Code validation (Standard 11 character format)
      const ifsc = actionData.ifscCode?.trim() || "";
      if (!ifsc) {
        toast.warning("IFSC code is required for offline payments.");
        return;
      }
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(ifsc.toUpperCase())) {
        toast.error("Invalid IFSC code format (e.g., SBIN0001234).");
        return;
      }
    }

    try {
      setActionLoading(true);
      // Prepare the approval data
      const approvalData = {
        ...actionData,
        // Map Online to ONLINE and Wallet to ONLINE (backward compatibility)
        paymentMethod: actionData.paymentMethod === "ONLINE" ? "ONLINE" : actionData.paymentMethod,
        // Only include customQuantity if it's provided and different from original
        ...(actionData.customQuantity &&
          parseInt(actionData.customQuantity) !==
          selectedRequest.requestedQuantity && {
          customQuantity: parseInt(actionData.customQuantity),
        }),
      };

      await requestOrderService.approveRequest(
        selectedRequest._id,
        approvalData
      );

      // Refresh data
      await fetchRequests();

      toast.success("Request approved successfully");
      setShowModal(false);
      setSelectedRequest(null);
      resetActionData();
    } catch (err) {
      toast.error(err.error || err.message || "Failed to approve request");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reject request
  const handleReject = async () => {
    if (!selectedRequest || !actionData.notes.trim()) return;

    try {
      setActionLoading(true);
      await requestOrderService.rejectRequest(selectedRequest._id, {
        notes: actionData.notes,
      });

      setNotificationWarning(null);

      // Refresh data
      await fetchRequests();

      toast.success("Request rejected successfully");
      setShowModal(false);
      setSelectedRequest(null);
      resetActionData();
    } catch (err) {
      toast.error(err.error || err.message || "Failed to reject request");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle confirm offline payment
  const handleConfirmPayment = async () => {
    if (!selectedRequest) return;

    try {
      setActionLoading(true);
      await requestOrderService.confirmOfflinePayment(selectedRequest._id, {
        paymentReference: actionData.paymentReference,
        notes: actionData.notes,
      });

      // Refresh data
      await fetchRequests();

      toast.success("Offline payment confirmed successfully");
      setShowModal(false);
      setSelectedRequest(null);
      resetActionData();
    } catch (err) {
      toast.error(err.error || err.message || "Failed to confirm payment");
    } finally {
      setActionLoading(false);
    }
  };

  const validateField = (name, value) => {
    let errors = { ...formErrors };

    if (name === "bankAccountNumber") {
      const bankAc = value?.trim() || "";
      if (!bankAc) {
        errors.bankAccountNumber = "Bank account number is required";
      } else if (!/^\d{9,18}$/.test(bankAc)) {
        errors.bankAccountNumber = "Account number must be 9-18 digits";
      } else {
        delete errors.bankAccountNumber;
      }
    }

    if (name === "ifscCode") {
      const ifsc = value?.trim() || "";
      if (!ifsc) {
        errors.ifscCode = "IFSC code is required";
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase())) {
        errors.ifscCode = "Invalid IFSC format (e.g., SBIN0001234)";
      } else {
        delete errors.ifscCode;
      }
    }

    if (name === "accountHolderName") {
      const nameVal = value?.trim() || "";
      const nameRegex = /^[A-Za-z\s.]+$/;
      if (!nameVal) {
        errors.accountHolderName = "Account holder name is required";
      } else if (nameVal.length < 3) {
        errors.accountHolderName = "Name must be at least 3 characters";
      } else if (!nameRegex.test(nameVal)) {
        errors.accountHolderName = "Name can only contain letters and spaces";
      } else {
        delete errors.accountHolderName;
      }
    }

    if (name === "bankName") {
      const bankVal = value?.trim() || "";
      const nameRegex = /^[A-Za-z\s.]+$/;
      if (!bankVal) {
        errors.bankName = "Bank name is required";
      } else if (bankVal.length < 3) {
        errors.bankName = "Bank name must be at least 3 characters";
      } else if (!nameRegex.test(bankVal)) {
        errors.bankName = "Bank name can only contain letters and spaces";
      } else {
        delete errors.bankName;
      }
    }

    setFormErrors(errors);
  };

  const resetActionData = () => {
    setFormErrors({});
    setActionData({
      customBuyPrice: "",
      notes: "",
      customQuantity: "",
      paymentMethod: "",
      paymentReference: "",
      bankAccountNumber: "",
      accountHolderName: "",
      ifscCode: "",
      bankName: "",
      processImmediately: false,
    });
  };

  const openModal = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setShowModal(true);

    // Pre-fill data for approve action
    if (action === "approve" && request.product) {
      setActionData((prev) => ({
        ...prev,
        customBuyPrice: request.product.buyPrice?.toString() || "",
        customQuantity: request.requestedQuantity?.toString() || "",
      }));
    }
  };
  const ConversionDisplay = () => {
    if (!selectedRequest) return null;

    const hasChanges =
      parseFloat(actionData.customBuyPrice) !==
      (selectedRequest.product?.buyPrice || 0) ||
      parseInt(actionData.customQuantity) !==
      (selectedRequest.requestedQuantity || 0);

    // Get wallet info from user or financialStatus
    const userWalletBalance =
      selectedRequest.user?.wallet?.balance ||
      selectedRequest.financialStatus?.walletBalance ||
      selectedRequest.metadata?.userWalletBalance ||
      0;

    const walletCurrency = selectedRequest.user?.wallet?.currency || "INR";
    const walletStatus = selectedRequest.user?.wallet?.status || "ACTIVE";

    // Calculate wallet sufficiency
    const requiredAmount = calculatedValues.totalAmount;
    const hasSufficientBalance = userWalletBalance >= requiredAmount;
    const balanceAfterTransaction = userWalletBalance - requiredAmount;
    const shortfall = hasSufficientBalance
      ? 0
      : requiredAmount - userWalletBalance;

    return (
      <div className="mt-4 space-y-3">
        {/* Wallet Status Card */}
        <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-indigo-900 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path
                  fillRule="evenodd"
                  d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                  clipRule="evenodd"
                />
              </svg>
              User Wallet Status
            </h4>
            <span
              className={`text-xs px-2 py-1 rounded-full font-medium ${walletStatus === "ACTIVE"
                ? "bg-green-100 text-green-800"
                : walletStatus === "FROZEN"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
                }`}
            >
              {walletStatus}
            </span>
          </div>

          <div className="space-y-2">
            {/* Current Balance */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-indigo-700">Current Balance:</span>
              <span className="text-lg font-bold text-indigo-900">
                {formatCurrency(userWalletBalance)}
              </span>
            </div>

            {/* Required Amount */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-indigo-700">Required Amount:</span>
              <span className="text-base font-semibold text-indigo-800">
                {formatCurrency(requiredAmount)}
              </span>
            </div>

            {/* Divider */}
            <div className="border-t border-indigo-200 my-2"></div>

            {/* Balance After Transaction */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-indigo-700">
                Balance After Transaction:
              </span>
              <span
                className={`text-lg font-bold ${hasSufficientBalance ? "text-green-600" : "text-red-600"
                  }`}
              >
                {formatCurrency(balanceAfterTransaction)}
              </span>
            </div>

            {/* Status Indicator */}
            {hasSufficientBalance ? (
              <div className="flex items-center mt-2 p-2 bg-green-50 rounded-lg">
                <svg
                  className="w-5 h-5 text-green-600 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-green-800 font-medium">
                  ✓ Sufficient balance available
                </span>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="flex items-start p-2 bg-red-50 rounded-lg">
                  <svg
                    className="w-5 h-5 text-red-600 mr-2 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <span className="text-sm text-red-800 font-medium block">
                      ⚠️ Insufficient wallet balance
                    </span>
                    <span className="text-xs text-red-700 block mt-1">
                      Shortfall: <strong>{formatCurrency(shortfall)}</strong>
                    </span>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="p-2 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-800">
                    💡 <strong>Recommendation:</strong> Use "OFFLINE" payment
                    method or ask user to add funds
                  </p>
                </div>
              </div>
            )}

            {/* Utilization Percentage */}
            {userWalletBalance > 0 && (
              <div className="mt-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-indigo-600">
                    Wallet Utilization:
                  </span>
                  <span className="text-xs font-semibold text-indigo-800">
                    {((requiredAmount / userWalletBalance) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-indigo-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${hasSufficientBalance
                      ? "bg-gradient-to-r from-green-500 to-green-600"
                      : "bg-gradient-to-r from-red-500 to-red-600"
                      }`}
                    style={{
                      width: `${Math.min(
                        (requiredAmount / userWalletBalance) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Conversion Summary */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-blue-900 mb-3">
            📊 Transaction Summary
          </h4>

          <div className="grid grid-cols-2 gap-4">
            {/* Original Values */}
            <div>
              <h5 className="text-xs font-medium text-blue-800 mb-2">
                Original Request
              </h5>
              <div className="space-y-1 text-xs text-blue-700">
                <div className="flex justify-between">
                  <span>Price/Share:</span>
                  <span>
                    {formatCurrency(selectedRequest.product?.buyPrice || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Quantity:</span>
                  <span>{selectedRequest.requestedQuantity || 0}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total:</span>
                  <span>
                    {formatCurrency(calculatedValues.originalTotalAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* New Values */}
            <div>
              <h5 className="text-xs font-medium text-blue-800 mb-2">
                {hasChanges ? "Updated Values" : "Current Values"}
              </h5>
              <div className="space-y-1 text-xs text-blue-700">
                <div className="flex justify-between">
                  <span>Price/Share:</span>
                  <span className={hasChanges ? "font-semibold" : ""}>
                    {formatCurrency(calculatedValues.pricePerShare)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Quantity:</span>
                  <span className={hasChanges ? "font-semibold" : ""}>
                    {calculatedValues.quantity}
                  </span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total:</span>
                  <span className={hasChanges ? "text-blue-900" : ""}>
                    {formatCurrency(calculatedValues.totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Show difference only if there are changes */}
          {hasChanges && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium text-blue-800">Difference:</span>
                <div className="text-right">
                  <div
                    className={`font-semibold ${calculatedValues.difference >= 0
                      ? "text-green-600"
                      : "text-red-600"
                      }`}
                  >
                    {calculatedValues.difference >= 0 ? "+" : ""}
                    {formatCurrency(calculatedValues.difference)}
                  </div>
                  <div
                    className={`text-xs ${calculatedValues.differencePercentage >= 0
                      ? "text-green-600"
                      : "text-red-600"
                      }`}
                  >
                    ({calculatedValues.differencePercentage >= 0 ? "+" : ""}
                    {calculatedValues.differencePercentage.toFixed(2)}%)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Razorpay Amount Warning */}
        {actionData.paymentMethod === "ONLINE" &&
          calculatedValues.totalAmount > 500000 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Amount exceeds Razorpay limit
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Razorpay typically has a limit of ₹5,00,000 per order.
                    Please consider using the <strong>OFFLINE</strong> payment
                    method for this transaction to avoid "Amount exceeds maximum
                    amount allowed" errors.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Payment Method Warning */}
        {!hasSufficientBalance && actionData.paymentMethod === "WALLET" && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  Cannot process with WALLET payment method
                </p>
                <p className="text-xs text-red-700 mt-1">
                  User has insufficient balance. Please switch to OFFLINE
                  payment method or ask user to add funds.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Financial Risk Assessment */}
        {selectedRequest.financialStatus && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <h5 className="text-xs font-semibold text-gray-700 mb-2">
              📈 Financial Risk Assessment
            </h5>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-600">Risk Level:</span>
                <span
                  className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${selectedRequest.financialStatus.riskLevel === "HIGH"
                    ? "bg-red-100 text-red-800"
                    : selectedRequest.financialStatus.riskLevel === "MEDIUM"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
                    }`}
                >
                  {selectedRequest.financialStatus.riskLevel}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Utilization:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {selectedRequest.financialStatus.balanceUtilizationPercent?.toFixed(
                    1
                  )}
                  %
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock },
      APPROVED: {
        bg: "bg-green-100",
        text: "text-green-800",
        icon: CheckCircle,
      },
      REJECTED: { bg: "bg-red-100", text: "text-red-800", icon: XCircle },
      PROCESSED: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        icon: CheckCircle,
      },
      CANCELLED: { bg: "bg-gray-100", text: "text-gray-800", icon: XCircle },
      PAYMENT_PENDING: {
        bg: "bg-orange-100",
        text: "text-orange-800",
        icon: Clock,
      },
      AWAITING_ONLINE_PAYMENT: {
        bg: "bg-amber-100",
        text: "text-amber-800",
        icon: IndianRupee,
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      HIGH: { bg: "bg-red-100", text: "text-red-800" },
      MEDIUM: { bg: "bg-yellow-100", text: "text-yellow-800" },
      LOW: { bg: "bg-green-100", text: "text-green-800" },
    };

    const config = priorityConfig[priority] || priorityConfig.MEDIUM;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}
      >
        {priority}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchRequests();
  }, [filters]);

  const shouldShowOnlinePaymentRequestLabel = () => {
    if (
      !selectedRequest ||
      actionType !== "approve" ||
      actionData.paymentMethod !== "ONLINE"
    ) {
      return false;
    }

    const userWalletBalance =
      selectedRequest.user?.wallet?.balance ??
      selectedRequest.financialStatus?.walletBalance ??
      selectedRequest.metadata?.userWalletBalance ??
      0;

    const requiredAmount =
      calculatedValues.totalAmount || selectedRequest.totalAmount || 0;

    return userWalletBalance < requiredAmount;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Approval Dashboard
          </h1>
          <p className="text-gray-600">
            Manage and process order approval requests
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.pendingCount || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.approvedCount || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Rejected</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.rejectedCount || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Requests
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalRequests || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search requests..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                        page: 1,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Status Filter */}
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: e.target.value,
                    page: 1,
                  }))
                }
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="PAYMENT_PENDING">Payment Pending</option>
                <option value="REJECTED">Rejected</option>
                <option value="PROCESSED">Processed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>

              {/* Priority Filter */}
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.priority}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    priority: e.target.value,
                    page: 1,
                  }))
                }
              >
                <option value="">All Priority</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>

              {/* Refresh Button */}
              <button
                onClick={() => {
                  fetchRequests();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Notification Warning */}
        {notificationWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-2" />
                <p className="text-amber-800 font-medium">{notificationWarning}</p>
              </div>
              <button
                onClick={() => setNotificationWarning(null)}
                className="text-amber-600 hover:text-amber-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Requests Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading requests...</span>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No approval requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Request Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          #{request._id?.slice(-8)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {request.requestType}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {request.user?.name || "N/A"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.user?.email || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {request.product?.name || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {request.product?.category || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(
                            request.adminResponse?.hasModifications
                              ? request.adminResponse.finalValues
                                ?.totalAmount ?? request.totalAmount
                              : request.totalAmount
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          Qty:{" "}
                          {request.adminResponse?.hasModifications
                            ? request.adminResponse.finalValues?.quantity ??
                            request.requestedQuantity
                            : request.requestedQuantity}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPriorityBadge(request.priority)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(request.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => fetchRequestDetails(request._id)}
                            disabled={detailsLoading}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                          >
                            {detailsLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>

                          {request.status === "PENDING" && (
                            <>
                              <button
                                onClick={() => openModal(request, "approve")}
                                className="text-green-600 hover:text-green-900"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openModal(request, "reject")}
                                className="text-red-600 hover:text-red-900"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {request.status === "PAYMENT_PENDING" && (
                            <button
                              onClick={() => openModal(request, "confirmPayment")}
                              className="text-orange-600 hover:text-orange-900"
                              title="Confirm Payment Received"
                            >
                              <IndianRupee className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && requests.length > 0 && pagination.total > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {(pagination.current - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.current * pagination.limit,
                    pagination.totalRecords
                  )}{" "}
                  of {pagination.totalRecords} results
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: prev.page - 1 }))
                    }
                    disabled={!pagination.hasPrev}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded">
                    Page {pagination.current} of {pagination.total}
                  </span>

                  <button
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                    }
                    disabled={!pagination.hasNext}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Request Details Modal */}
        {showDetailsModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
              {/* Header (not scrollable) */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Request Details
                </h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedRequest(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="p-6 overflow-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Request & User Info */}
                  <div className="space-y-4">
                    {/* Request Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Request Information
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Request ID:
                          </span>
                          <span className="text-sm text-gray-900 font-mono">
                            #{selectedRequest._id?.slice(-12)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Type:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.requestType}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Status:
                          </span>
                          <div>{getStatusBadge(selectedRequest.status)}</div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Priority:
                          </span>
                          <div>
                            {getPriorityBadge(selectedRequest.priority)}
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Created:
                          </span>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedRequest.createdAt)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Updated:
                          </span>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedRequest.updatedAt)}
                          </span>
                        </div>
                        {selectedRequest.requestReason && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">
                              Reason:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedRequest.requestReason || "N/A"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Computed Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Request Analysis
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Request Age:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.computed?.requestAge || 0} hours
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Risk Level:
                          </span>
                          <span
                            className={`text-sm px-2 py-1 rounded text-xs font-medium ${selectedRequest.computed?.riskLevel === "HIGH"
                              ? "bg-red-100 text-red-800"
                              : selectedRequest.computed?.riskLevel ===
                                "MEDIUM"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                              }`}
                          >
                            {selectedRequest.computed?.riskLevel || "LOW"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Can Approve:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.computed?.canBeApproved ? (
                              <span className="text-green-600">✓ Yes</span>
                            ) : (
                              <span className="text-red-600">✗ No</span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Can Reject:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.computed?.canBeRejected ? (
                              <span className="text-green-600">✓ Yes</span>
                            ) : (
                              <span className="text-red-600">✗ No</span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Expired:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.computed?.isExpired ? (
                              <span className="text-red-600">✓ Yes</span>
                            ) : (
                              <span className="text-green-600">✗ No</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* User Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        User Information
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Name:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.user?.name || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Email:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.user?.email || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Mobile:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.user?.mobileNo || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            KYC Verified:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.user?.isKycVerified ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-red-600">
                                ✗ Not Verified
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Wallet Balance:
                          </span>
                          <span className="text-sm text-gray-900 font-semibold">
                            {formatCurrency(
                              selectedRequest.user?.walletBalance || 0
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Product & Financial Info */}
                  <div className="space-y-4">
                    {/* Product Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Product Information
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Product:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.product?.name || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Category:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.product?.category || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Buy Price:
                          </span>
                          <span className="text-sm text-gray-900 font-semibold">
                            {formatCurrency(
                              selectedRequest.product?.buyPrice || 0
                            )}
                          </span>
                        </div>
                        {selectedRequest.product?.description && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">
                              Description:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedRequest.product.description || "N/A"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Financial Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Financial Details
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Requested Quantity:
                          </span>
                          <span className="text-sm text-gray-900 font-semibold">
                            {selectedRequest.requestedQuantity}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Price per Share:
                          </span>
                          <span className="text-sm text-gray-900 font-semibold">
                            {formatCurrency(selectedRequest.pricePerShare)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Total Amount:
                          </span>
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(selectedRequest.totalAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            User Wallet Balance:
                          </span>
                          <span className="text-sm text-gray-900">
                            {formatCurrency(
                              selectedRequest.metadata?.userWalletBalance || 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Sufficient Balance:
                          </span>
                          <span className="text-sm text-gray-900">
                            {(selectedRequest.metadata?.userWalletBalance ||
                              0) >= selectedRequest.totalAmount ? (
                              <span className="text-green-600">✓ Yes</span>
                            ) : (
                              <span className="text-red-600">✗ No</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Portfolio Information */}
                    {selectedRequest.portfolio && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">
                          Portfolio Information
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-600">
                              Portfolio:
                            </span>
                            <span className="text-sm text-gray-900">
                              {selectedRequest.portfolio.name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-600">
                              Type:
                            </span>
                            <span className="text-sm text-gray-900">
                              {selectedRequest.portfolio.type}
                            </span>
                          </div>
                          {selectedRequest.portfolio.summary && (
                            <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-gray-600">
                                  Total Investment:
                                </span>
                                <span className="text-sm text-gray-900">
                                  {formatCurrency(
                                    selectedRequest.portfolio.summary
                                      .totalInvestment ?? 0
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-gray-600">
                                  Current Value:
                                </span>
                                <span className="text-sm text-gray-900">
                                  {formatCurrency(
                                    selectedRequest.portfolio.summary
                                      .currentValue ?? 0
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-gray-600">
                                  Total Profit:
                                </span>
                                <span className="text-sm text-gray-900">
                                  {formatCurrency(
                                    selectedRequest.portfolio.summary
                                      .totalProfit ?? 0
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-gray-600">
                                  Profit %:
                                </span>
                                <span className="text-sm text-gray-900">
                                  {selectedRequest.portfolio.summary
                                    .profitPercentage ?? "N/A"}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-gray-600">
                                  Last Valuation:
                                </span>
                                <span className="text-sm text-gray-900">
                                  {selectedRequest.portfolio.summary
                                    .lastValuationDate
                                    ? formatDate(
                                      selectedRequest.portfolio.summary
                                        .lastValuationDate
                                    )
                                    : "N/A"}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Admin Response & Changes */}
                  <div className="space-y-4">
                    {/* Admin Response */}
                    {selectedRequest.adminResponse && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">
                          Admin Response
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-600">
                              Decision:
                            </span>
                            <span
                              className={`text-sm px-2 py-1 rounded text-xs font-medium ${selectedRequest.adminResponse.decision ===
                                "APPROVED"
                                ? "bg-green-100 text-green-800"
                                : selectedRequest.adminResponse.decision ===
                                  "REJECTED"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                                }`}
                            >
                              {selectedRequest.adminResponse.decision}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-600">
                              Decided At:
                            </span>
                            <span className="text-sm text-gray-900">
                              {selectedRequest.adminResponse.decidedAt
                                ? formatDate(
                                  selectedRequest.adminResponse.decidedAt
                                )
                                : "N/A"}
                            </span>
                          </div>

                          {selectedRequest.adminResponse.reason && (
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                Reason:
                              </span>
                              <p className="text-sm text-gray-900 mt-1">
                                {selectedRequest.adminResponse.reason}
                              </p>
                            </div>
                          )}
                          {selectedRequest.adminResponse.notes && (
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                Notes:
                              </span>
                              <p className="text-sm text-gray-900 mt-1">
                                {selectedRequest.adminResponse.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Payment Information */}
                    {selectedRequest.adminResponse?.paymentMethod && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">
                          Payment Information
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-600">
                              Payment Method:
                            </span>
                            <span className="text-sm text-gray-900">
                              {selectedRequest.adminResponse.paymentMethod}
                            </span>
                          </div>
                          {selectedRequest.adminResponse.accountHolderName && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">
                                Account Holder:
                              </span>
                              <span className="text-sm text-gray-900 font-semibold">
                                {selectedRequest.adminResponse.accountHolderName}
                              </span>
                            </div>
                          )}
                          {selectedRequest.adminResponse.bankAccountNumber && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">
                                Bank Account:
                              </span>
                              <span className="text-sm text-gray-900 font-semibold">
                                {selectedRequest.adminResponse.bankAccountNumber}
                              </span>
                            </div>
                          )}
                          {selectedRequest.adminResponse.ifscCode && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">
                                IFSC Code:
                              </span>
                              <span className="text-sm text-gray-900 font-semibold uppercase">
                                {selectedRequest.adminResponse.ifscCode}
                              </span>
                            </div>
                          )}
                          {selectedRequest.adminResponse.bankName && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">
                                Bank Name:
                              </span>
                              <span className="text-sm text-gray-900 font-semibold">
                                {selectedRequest.adminResponse.bankName}
                              </span>
                            </div>
                          )}
                          {selectedRequest.adminResponse.paymentReference && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">
                                Reference:
                              </span>
                              <span className="text-sm text-gray-900 font-mono">
                                {selectedRequest.adminResponse.paymentReference}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Modifications & Changes */}
                    {selectedRequest.adminResponse?.hasModifications && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">
                          Modifications Made
                        </h3>
                        <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                          {selectedRequest.adminResponse.priceModified && (
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-amber-800">
                                  Price Changed:
                                </span>
                                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded">
                                  Modified
                                </span>
                              </div>
                              <div className="text-sm text-amber-700 space-y-1">
                                <div className="flex justify-between">
                                  <span>Original:</span>
                                  <span>
                                    {formatCurrency(
                                      selectedRequest.adminResponse
                                        .originalPrice
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>New:</span>
                                  <span>
                                    {formatCurrency(
                                      selectedRequest.adminResponse
                                        .customBuyPrice
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between font-semibold">
                                  <span>Change:</span>
                                  <span
                                    className={
                                      selectedRequest.adminResponse.changes
                                        .priceChange >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }
                                  >
                                    {selectedRequest.adminResponse.changes
                                      .priceChange >= 0
                                      ? "+"
                                      : ""}
                                    {formatCurrency(
                                      selectedRequest.adminResponse.changes
                                        .priceChange
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {selectedRequest.adminResponse.quantityModified && (
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-amber-800">
                                  Quantity Changed:
                                </span>
                              </div>
                              <div className="text-sm text-amber-700 space-y-1">
                                <div className="flex justify-between">
                                  <span>Original:</span>
                                  <span>
                                    {
                                      selectedRequest.adminResponse
                                        .originalQuantity
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>New:</span>
                                  <span>
                                    {
                                      selectedRequest.adminResponse
                                        .customQuantity
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between font-semibold">
                                  <span>Change:</span>
                                  <span
                                    className={
                                      selectedRequest.adminResponse.changes
                                        .quantityChange >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }
                                  >
                                    {selectedRequest.adminResponse.changes
                                      .quantityChange >= 0
                                      ? "+"
                                      : ""}
                                    {
                                      selectedRequest.adminResponse.changes
                                        .quantityChange
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Final Approved Values */}
                          <div className="border-t border-amber-200 pt-3">
                            <h4 className="text-sm font-medium text-amber-800 mb-2">
                              Final Approved Values:
                            </h4>
                            <div className="text-sm text-amber-700 space-y-1">
                              <div className="flex justify-between">
                                <span>Price:</span>
                                <span className="font-semibold">
                                  {formatCurrency(
                                    selectedRequest.adminResponse.finalValues
                                      .price
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Quantity:</span>
                                <span className="font-semibold">
                                  {
                                    selectedRequest.adminResponse.finalValues
                                      .quantity
                                  }
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Total:</span>
                                <span className="font-semibold">
                                  {formatCurrency(
                                    selectedRequest.adminResponse.finalValues
                                      .totalAmount
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Additional Information
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            User KYC Status:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.metadata?.userKycStatus ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-red-600">
                                ✗ Not Verified
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Portfolio Type:
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedRequest.metadata?.portfolioType || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Modal */}
        {showModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {actionType === "approve" && "Approve Request"}
                  {actionType === "reject" && "Reject Request"}
                  {actionType === "confirmPayment" && "Confirm Offline Payment"}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRequest(null);
                    resetActionData();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="p-6 overflow-auto">
                <div className="space-y-4">
                  {/* Request Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">
                      Request Summary
                    </h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Product: {selectedRequest.product?.name}</p>
                      <p>Quantity: {selectedRequest.requestedQuantity}</p>
                      <p>
                        Total Amount:{" "}
                        {formatCurrency(selectedRequest.totalAmount)}
                      </p>
                      <p>User: {selectedRequest.user?.name}</p>
                    </div>
                  </div>

                  {/* Approve Form */}
                  {actionType === "approve" && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Buy Price (Price / unit) (Optional)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Enter custom price"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={actionData.customBuyPrice}
                          onChange={(e) =>
                            setActionData((prev) => ({
                              ...prev,
                              customBuyPrice: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Quantity (Optional)
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          placeholder={`Default: ${selectedRequest.requestedQuantity}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={actionData.customQuantity}
                          onChange={(e) =>
                            setActionData((prev) => ({
                              ...prev,
                              customQuantity: e.target.value,
                            }))
                          }
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Leave empty to use requested quantity:{" "}
                          {selectedRequest.requestedQuantity}
                        </p>
                      </div>
                      <ConversionDisplay />

                      {shouldShowOnlinePaymentRequestLabel() && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-800">
                            <strong>Online Payment Requested:</strong> User
                            does not have sufficient wallet balance. Approving
                            with <strong>Online</strong> will create a Razorpay
                            payment request instead of deducting their wallet
                            now.
                          </p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Method
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={actionData.paymentMethod}
                          onChange={(e) => {
                            setActionData((prev) => ({
                              ...prev,
                              paymentMethod: e.target.value,
                            }));
                          }}
                        >
                          <option value="">Select payment method</option>
                          <option value="OFFLINE">
                            Offline Payment (Bank Transfer)
                          </option>
                          <option value="ONLINE">Online Payment (Wallet Auto Deduct)</option>
                        </select>
                        {/* Bank Account field - show only for offline payments */}
                        {actionData.paymentMethod === "OFFLINE" && (
                          <div className="mt-4 space-y-4">
                            {/* User's KYC Bank Details Reference */}
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <h5 className="text-xs font-bold text-blue-800 mb-2 flex items-center">
                                <Info className="h-4 w-4 mr-2" />
                                User's Registered KYC Bank Details
                              </h5>
                              {selectedRequest?.user?.bankDetails ? (
                                <div className="grid grid-cols-2 gap-2 text-[10px] bg-white p-2 rounded border border-blue-100">
                                  <div>
                                    <p className="text-gray-500 font-bold uppercase">Account Name</p>
                                    <p className="font-medium text-gray-900">{selectedRequest.user.bankDetails.accountName || "N/A"}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 font-bold uppercase">Bank Name</p>
                                    <p className="font-medium text-gray-900">{selectedRequest.user.bankDetails.bankName || "N/A"}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 font-bold uppercase">Account Number</p>
                                    <p className="font-mono font-bold text-gray-900">{selectedRequest.user.bankDetails.accountNumber || "N/A"}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 font-bold uppercase">IFSC Code</p>
                                    <p className="font-mono font-bold text-gray-900">{selectedRequest.user.bankDetails.ifscCode || "N/A"}</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-2 bg-amber-50 border border-amber-100 rounded text-amber-700 text-[10px] flex items-center">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  User has no bank details in KYC.
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Account Holder Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                className={`w-full px-3 py-2 border ${formErrors.accountHolderName ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"} rounded-lg focus:ring-2`}
                                placeholder="Enter account holder name"
                                value={actionData.accountHolderName || ""}
                                onChange={(e) => {
                                  setActionData((prev) => ({
                                    ...prev,
                                    accountHolderName: e.target.value,
                                  }));
                                  validateField("accountHolderName", e.target.value);
                                }}
                              />
                              {formErrors.accountHolderName && (
                                <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.accountHolderName}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bank Account Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                required
                                className={`w-full px-3 py-2 border ${formErrors.bankAccountNumber ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"} rounded-lg focus:ring-2`}
                                placeholder="Enter 9-18 digit account number"
                                maxLength={18}
                                value={actionData.bankAccountNumber || ""}
                                onChange={(e) => {
                                  // Only allow numbers
                                  const val = e.target.value.replace(/\D/g, "");
                                  setActionData((prev) => ({
                                    ...prev,
                                    bankAccountNumber: val,
                                  }));
                                  validateField("bankAccountNumber", val);
                                }}
                              />
                              {formErrors.bankAccountNumber && (
                                <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.bankAccountNumber}</p>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  IFSC Code <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  required
                                  className={`w-full px-3 py-2 border ${formErrors.ifscCode ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"} rounded-lg focus:ring-2 uppercase`}
                                  placeholder="SBIN0001234"
                                  maxLength={11}
                                  value={actionData.ifscCode || ""}
                                  onChange={(e) => {
                                    const val = e.target.value.toUpperCase();
                                    setActionData((prev) => ({
                                      ...prev,
                                      ifscCode: val,
                                    }));
                                    validateField("ifscCode", val);
                                  }}
                                />
                                {formErrors.ifscCode && (
                                  <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.ifscCode}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Bank Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`w-full px-3 py-2 border ${formErrors.bankName ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"} rounded-lg focus:ring-2`}
                                  placeholder="Name of the bank"
                                  value={actionData.bankName || ""}
                                  onChange={(e) => {
                                    setActionData((prev) => ({
                                      ...prev,
                                      bankName: e.target.value,
                                    }));
                                    validateField("bankName", e.target.value);
                                  }}
                                />
                                {formErrors.bankName && (
                                  <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.bankName}</p>
                                )}
                              </div>
                            </div>

                            <p className="text-xs text-blue-600 font-medium bg-blue-50 p-2 rounded">
                              ℹ️ These bank details will be shared with the user for their offline transfer.
                            </p>
                          </div>
                        )}

                        {/* Payment method info */}
                        {actionData.paymentMethod && (
                          <div className="mt-2 text-sm">
                            {actionData.paymentMethod === "OFFLINE" ? (
                              <div className="flex items-center text-amber-600">
                                <svg
                                  className="w-4 h-4 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Request will go to 'Payment Pending' status.
                              </div>
                            ) : (
                              // ONLINE logic
                              shouldShowOnlinePaymentRequestLabel() ? (
                                <div className="flex items-center text-amber-600">
                                  <svg
                                    className="w-4 h-4 mr-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Insufficient wallet balance detected; a Razorpay payment request will be initiated for the user.
                                </div>
                              ) : (
                                <div className="flex items-center text-green-600">
                                  <svg
                                    className="w-4 h-4 mr-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Amount will be deducted from user wallet immediately.
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                  {/* Confirm Payment Form */}
                  {actionType === "confirmPayment" && (
                    <div className="space-y-4">
                      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-orange-800">
                          <strong>Instructions:</strong> Confirm that you have received the offline payment for this request.
                        </p>
                        {selectedRequest.adminResponse?.bankAccountNumber && (
                          <div className="mt-2 space-y-1 bg-white bg-opacity-50 p-2 rounded-md border border-orange-200">
                            <p className="text-xs text-orange-900"><strong>Bank Details Provided:</strong></p>
                            {selectedRequest.adminResponse.accountHolderName && (
                              <p className="text-sm text-orange-900">Name: <strong>{selectedRequest.adminResponse.accountHolderName}</strong></p>
                            )}
                            <p className="text-sm text-orange-900">A/C: <strong>{selectedRequest.adminResponse.bankAccountNumber}</strong></p>
                            {selectedRequest.adminResponse.ifscCode && (
                              <p className="text-sm text-orange-900">IFSC: <strong>{selectedRequest.adminResponse.ifscCode}</strong></p>
                            )}
                            {selectedRequest.adminResponse.bankName && (
                              <p className="text-sm text-orange-900">Bank: <strong>{selectedRequest.adminResponse.bankName}</strong></p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Reference / UTR Number
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter transaction reference"
                          value={actionData.paymentReference}
                          onChange={(e) =>
                            setActionData((prev) => ({
                              ...prev,
                              paymentReference: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Admin Notes (Optional)
                        </label>
                        <textarea
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Add any internal notes..."
                          value={actionData.notes}
                          onChange={(e) =>
                            setActionData((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                        ></textarea>
                      </div>
                    </div>
                  )}

                  {/* Reject Form */}
                  {actionType === "reject" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rejection Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={4}
                        placeholder="Please provide a reason for rejection"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={actionData.notes}
                        onChange={(e) =>
                          setActionData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Footer (always visible) */}
              <div className="p-4 border-t border-gray-200 bg-white flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRequest(null);
                    resetActionData();
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    if (actionType === "approve") handleApprove();
                    else if (actionType === "reject") handleReject();
                    else if (actionType === "confirmPayment") handleConfirmPayment();
                  }}
                  disabled={
                    actionLoading ||
                    (actionType === "reject" && !actionData.notes.trim()) ||
                    (actionType === "approve" && (
                      !actionData.paymentMethod ||
                      (actionData.paymentMethod === "OFFLINE" && (
                        !actionData.bankAccountNumber ||
                        !actionData.ifscCode ||
                        !actionData.accountHolderName ||
                        !actionData.bankName ||
                        Object.keys(formErrors).length > 0
                      ))
                    ))
                  }
                  className={`w-full py-2 px-4 rounded-lg text-white font-medium transition-colors flex items-center justify-center ${actionType === "reject"
                    ? "bg-red-600 hover:bg-red-700"
                    : actionType === "confirmPayment"
                      ? "bg-orange-600 hover:bg-orange-700"
                      : "bg-blue-600 hover:bg-blue-700"
                    } disabled:opacity-50`}
                >
                  {actionLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : null}
                  {actionType === "approve" &&
                    (shouldShowOnlinePaymentRequestLabel()
                      ? "Request Online Payment"
                      : "Confirm Approval")}
                  {actionType === "reject" && "Confirm Rejection"}
                  {actionType === "confirmPayment" && "Confirm Payment & Fulfill"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApprovalDashboard;
